import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

export interface IGetNotesParams {
	credentialId: number;
}

export interface IGetNotesResponse {
	credentialId: number;
	notes: string | null;
}

interface IGetNotesState {
	loading: boolean;
	error: string | null;
	data: IGetNotesResponse | null;
}

const initialState: IGetNotesState = {
	loading: false,
	error: null,
	data: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
	};
};

const buildUrl = (credentialId: number) => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Credentials/GetNotes?credentialId=${credentialId}`;
};

export const getNotes = createAsyncThunk<IGetNotesResponse, IGetNotesParams>(
	"passwordManager/getNotes",
	async ({ credentialId }, { rejectWithValue, dispatch }) => {
		try {
			const response = await withPasswordManagerRefresh(dispatch, () =>
				axios.get<IGetNotesResponse>(buildUrl(credentialId), { headers: getAuthHeaders() }),
			);
			const payload = response?.data;
			if (!payload || typeof payload !== "object") {
				return { credentialId, notes: "" };
			}
			return {
				credentialId: payload.credentialId ?? credentialId,
				notes: payload.notes ?? "",
			};
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to fetch notes";
			return rejectWithValue(message);
		}
	},
);

const getNotesSlice = createSlice({
	name: "passwordManager/getNotes",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(getNotes.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getNotes.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.data = action.payload;
			})
			.addCase(getNotes.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to fetch notes";
			});
	},
});

export const getNotesReducer = getNotesSlice.reducer;
